/**
 * Resultado POR LINHA da versão (DEMO-03, telas/ResultadoLinhas.tsx): item de
 * Venda ou componente de Locação/Trade-in. Ganha + perdida + pendente =
 * quantidade proposta; só a quantidade GANHA entra no Pedido.
 */
import {
  RESULTADO_LINHA,
  aplicarLinhas,
  classificar,
  entidadeDe,
  linhasResultadoDaVersao,
  motivoPerdaExigeObservacao,
  parceiroSelecionado,
  produtoDoItem,
  resumoLinhas,
  textoResumo,
  tipoDaVersao,
  validarLinhas,
  type LinhaInformada,
  type Versao,
} from '../../../../backend/src/compartilhado/propostas.ts';
import { useProposta } from './contexto.tsx';

export function TabelaResultado({ v, linhas, setLinhas, motivos }: { v: Versao; linhas: LinhaInformada[]; setLinhas: (l: LinhaInformada[]) => void; motivos: string[] }) {
  const { d } = useProposta();
  const conjunto = tipoDaVersao(v) !== 'venda';
  const parceiro = parceiroSelecionado(v);
  const nome = (l: ReturnType<typeof linhasResultadoDaVersao>[number]) => {
    if ('produtoId' in l && !conjunto) {
      const p = produtoDoItem(d, l as unknown as { produtoId: string });
      return `${p.sku} — ${p.nome}`;
    }
    return (l as unknown as { descricao: string }).descricao;
  };
  const fornecedor = (l: ReturnType<typeof linhasResultadoDaVersao>[number]) => {
    const id = !conjunto && 'entidadeId' in l ? (l as { entidadeId: string }).entidadeId : parceiro;
    return id ? (entidadeDe(d, id)?.fantasia ?? '—') : '— sem fornecedor';
  };
  const set = (id: string, patch: Partial<LinhaInformada>) => setLinhas(linhas.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const erros = validarLinhas(v, linhas);
  const r = resumoLinhas(aplicarLinhas(v, linhas));
  return (
    <div data-teste="tabela-resultado">
      {conjunto && (
        <div className="barra-ferramentas">
          <span className="texto-secundario">Operação tratada como conjunto (valor fechado/avaliação do lote). SKU não é obrigatório.</span>
          <span className="espaco" />
          <button type="button" className="botao-secundario pequeno" onClick={() => setLinhas(linhasResultadoDaVersao(v).map((l) => ({ id: l.id, ganha: l.quantidade, perdida: 0, motivo: '', observacao: '' })))} data-teste="conjunto-ganho">
            Conjunto: tudo ganho
          </button>
          <button type="button" className="botao-secundario pequeno" onClick={() => setLinhas(linhasResultadoDaVersao(v).map((l) => ({ id: l.id, ganha: 0, perdida: l.quantidade, motivo: motivos[0] ?? '', observacao: '' })))} data-teste="conjunto-perdido">
            Conjunto: tudo perdido
          </button>
        </div>
      )}
      <div className="rolagem">
        <table className="tabela tabela-compacta">
          <thead>
            <tr>
              <th>{conjunto ? 'Componente/lote' : 'Item'}</th>
              <th>Qtd proposta</th>
              <th>Qtd ganha</th>
              <th>Qtd perdida</th>
              <th>Qtd pendente</th>
              <th>Resultado</th>
              <th>Motivo da perda</th>
              <th>{conjunto ? 'Parceiro selecionado' : 'Fornecedor selecionado'}</th>
              <th>Observação</th>
            </tr>
          </thead>
          <tbody>
            {linhasResultadoDaVersao(v).map((l, n) => {
              const x = linhas.find((y) => y.id === l.id) ?? { id: l.id, ganha: 0, perdida: 0 };
              const pendente = l.quantidade - x.ganha - x.perdida;
              return (
                <tr key={l.id} data-teste={`resultado-${n + 1}`}>
                  <td>{nome(l)}</td>
                  <td>{l.quantidade}</td>
                  <td>
                    <input type="number" min={0} max={l.quantidade} value={x.ganha} onChange={(e) => set(l.id, { ganha: Math.max(0, Number(e.target.value)) })} aria-label={`Quantidade ganha da linha ${n + 1}`} className="entrada-curta" data-teste={`ganha-${n + 1}`} />
                  </td>
                  <td>
                    <input type="number" min={0} max={l.quantidade} value={x.perdida} onChange={(e) => set(l.id, { perdida: Math.max(0, Number(e.target.value)) })} aria-label={`Quantidade perdida da linha ${n + 1}`} className="entrada-curta" data-teste={`perdida-${n + 1}`} />
                  </td>
                  <td className={pendente < 0 ? 'diferenca-negativa' : ''}>{pendente}</td>
                  <td>
                    <span className="selo selo-neutro">{pendente < 0 ? 'inválido' : RESULTADO_LINHA[classificar(l.quantidade, x.ganha, x.perdida)]}</span>
                  </td>
                  <td>
                    <select value={x.motivo ?? ''} onChange={(e) => set(l.id, { motivo: e.target.value })} disabled={x.perdida === 0} aria-label={`Motivo da perda da linha ${n + 1}`} data-teste={`motivo-${n + 1}`}>
                      <option value="">—</option>
                      {motivos.map((m) => (
                        <option key={m}>{m}</option>
                      ))}
                    </select>
                  </td>
                  <td>{fornecedor(l)}</td>
                  <td>
                    <input value={x.observacao ?? ''} onChange={(e) => set(l.id, { observacao: e.target.value })} aria-label={`Observação da linha ${n + 1}`} placeholder={x.perdida > 0 && motivoPerdaExigeObservacao(x.motivo ?? '') ? 'obrigatória (“Outro”)' : undefined} className="entrada-media" data-teste={`observacao-${n + 1}`} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="texto-secundario" data-teste="resumo-resultado">
        {textoResumo(r)}. Só a quantidade ganha entra no Pedido; perdidos e pendentes ficam fora.
      </p>
      {conjunto && tipoDaVersao(v) === 'locacao' && r.algumGanho && !r.tudoGanho && (
        <p className="aviso aviso-alerta" data-teste="aviso-conjunto">
          Locação tem valor do conjunto (preço fechado pelo parceiro). Aceite parcial exige nova versão com o valor revisado; nenhum Pedido é gerado com o conjunto incompleto.
        </p>
      )}
      {erros.length > 0 && (
        <ul className="aviso aviso-alerta" role="alert" data-teste="erros-resultado">
          {erros.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
